import React, { useState, useEffect, useCallback } from 'react';
import { licenseService, sitesService, getErrorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Currency symbols
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  TRY: '₺',
  GBP: '£'
};

// Custom hook for license stats
const useLicenseStats = () => {
  const getToken = () => localStorage.getItem('hims_token');
  
  return {
    getStats: async () => {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/licenses/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Stats fetch failed');
      return response.json();
    },
    sendWarnings: async () => {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/licenses/send-expiry-warnings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to send warnings');
      }
      return response.json();
    }
  };
};

// Native Modal Component - CSS değişkenleri ile tema desteği
const NativeModal = ({ isOpen, onClose, title, children, disabled }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !disabled) {
          onClose();
        }
      }}
      data-testid="modal-backdrop"
    >
      <div
        style={{
          backgroundColor: 'var(--card-bg)',
          color: 'var(--text-primary)',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          width: '100%',
          maxWidth: '640px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid var(--card-border)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--card-border)',
            background: 'linear-gradient(to right, #2563eb, #1d4ed8)'
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'white', margin: 0 }}>
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            style={{
              padding: '6px',
              borderRadius: '6px',
              border: 'none',
              background: 'rgba(255,255,255,0.2)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              color: 'white'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, color: 'var(--text-primary)' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Native Button Component
const NativeButton = ({ children, onClick, type = 'button', variant = 'primary', disabled, style = {}, ...props }) => {
  const baseStyle = {
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: 'none',
    transition: 'all 0.2s',
    opacity: disabled ? 0.6 : 1
  };

  const variants = {
    primary: { backgroundColor: '#2563eb', color: 'white' },
    secondary: { backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' },
    destructive: { backgroundColor: '#dc2626', color: 'white' },
    ghost: { backgroundColor: 'transparent', color: '#374151' }
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{ ...baseStyle, ...variants[variant], ...style }}
      {...props}
    >
      {children}
    </button>
  );
};

// Native Input Component - CSS değişkenleri ile tema desteği
const NativeInput = ({ label, id, required, labelStyle, ...props }) => (
  <div style={{ marginBottom: '12px' }}>
    {label && (
      <span 
        style={{ 
          display: 'block', 
          marginBottom: '6px',
          fontWeight: '600', 
          fontSize: '14px',
          ...labelStyle
        }}
      >
        {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
      </span>
    )}
    <input
      id={id}
      style={{
        color: 'var(--text-primary)',
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        padding: '10px 12px',
        borderRadius: '6px',
        width: '100%',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box'
      }}
      {...props}
    />
  </div>
);

// Native Select Component - CSS değişkenleri ile tema desteği
const NativeSelect = ({ label, id, options, labelStyle, ...props }) => (
  <div style={{ marginBottom: '12px' }}>
    {label && (
      <span 
        style={{ 
          display: 'block', 
          marginBottom: '6px',
          fontWeight: '600', 
          fontSize: '14px',
          ...labelStyle
        }}
      >
        {label}
      </span>
    )}
    <select
      id={id}
      style={{
        color: 'var(--text-primary)',
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        padding: '10px 12px',
        borderRadius: '6px',
        width: '100%',
        fontSize: '14px',
        outline: 'none',
        boxSizing: 'border-box'
      }}
      {...props}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

// Native Textarea Component - CSS değişkenleri ile tema desteği
const NativeTextarea = ({ label, id, labelStyle, ...props }) => (
  <div style={{ marginBottom: '12px' }}>
    {label && (
      <span 
        style={{ 
          display: 'block', 
          marginBottom: '6px',
          fontWeight: '600', 
          fontSize: '14px',
          ...labelStyle
        }}
      >
        {label}
      </span>
    )}
    <textarea
      id={id}
      style={{
        color: 'var(--text-primary)',
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        padding: '10px 12px',
        borderRadius: '6px',
        width: '100%',
        fontSize: '14px',
        outline: 'none',
        resize: 'none',
        boxSizing: 'border-box'
      }}
      {...props}
    />
  </div>
);

// Native Checkbox Component - CSS değişkenleri ile tema desteği
const NativeCheckbox = ({ label, id, checked, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onChange}
      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
    />
    <label htmlFor={id} style={{ color: 'var(--text-primary)', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>{label}</label>
  </div>
);

export default function LicensesPage() {
  const { isSuperAdmin, user, getEffectiveRole, getEffectiveSiteId, isHesapYoneticisi } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingWarnings, setSendingWarnings] = useState(false);
  const [dismissedWarnings, setDismissedWarnings] = useState([]);
  
  // Efektif rol kontrolü - simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : user?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : user?.site_id;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  const isEffectiveAdmin = ['super_admin', 'admin'].includes(effectiveRole);
  const isEffectiveSuperAdmin = effectiveRole === 'super_admin';
  
  const licenseStats = useLicenseStats();
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingLicenseId, setEditingLicenseId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingLicense, setDeletingLicense] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Form state (license_key removed for create mode)
  const [form, setForm] = useState({
    site_id: '',
    max_stations: 10,
    expires_at: '',
    is_active: true,
    price: 0,
    currency: 'USD',
    notes: '',
    warn_days_before: 30,
    warning_method: 'both',
    warning_message: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let [licensesData, sitesData, statsData] = await Promise.all([
        licenseService.list(),
        sitesService.list(),
        licenseStats.getStats().catch(() => null)
      ]);
      
      // Hesap Yöneticisi için sadece kendi sitesinin lisanslarını göster
      if ((isEffectiveHesapYoneticisi || isHesapYoneticisi()) && effectiveSiteId) {
        licensesData = (licensesData || []).filter(lic => lic.site_id === effectiveSiteId);
        sitesData = (sitesData || []).filter(s => s.id === effectiveSiteId);
      }
      
      setLicenses(licensesData || []);
      setSites(sitesData || []);
      setStats(statsData);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [isEffectiveHesapYoneticisi, isHesapYoneticisi, effectiveSiteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get licenses that need warnings shown on screen
  const getWarningLicenses = () => {
    const now = new Date();
    return licenses.filter(lic => {
      if (!lic.expires_at || !lic.is_active) return false;
      if (dismissedWarnings.includes(lic.id)) return false;
      
      const expiresAt = new Date(lic.expires_at);
      const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
      const warnDays = lic.warn_days_before || 30;
      
      return daysRemaining > 0 && daysRemaining <= warnDays;
    });
  };

  const dismissWarning = (licenseId) => {
    setDismissedWarnings(prev => [...prev, licenseId]);
  };

  const resetForm = () => {
    setForm({
      site_id: '',
      max_stations: 10,
      expires_at: '',
      is_active: true,
      price: 0,
      currency: 'USD',
      notes: '',
      warn_days_before: 30,
      warning_method: 'both',
      warning_message: ''
    });
  };

  const openCreateModal = () => {
    resetForm();
    setEditingLicenseId(null);
    setModalMode('create');
    setSaving(false);
    setModalOpen(true);
  };

  const openEditModal = (license) => {
    setForm({
      site_id: license.site_id || '',
      max_stations: license.max_stations || 10,
      expires_at: license.expires_at ? license.expires_at.split('T')[0] : '',
      is_active: license.is_active ?? true,
      price: license.price || 0,
      currency: license.currency || 'USD',
      notes: license.notes || '',
      warn_days_before: license.warn_days_before || 30,
      warning_method: license.warning_method || 'both',
      warning_message: license.warning_message || ''
    });
    setEditingLicenseId(license.id);
    setModalMode('edit');
    setSaving(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingLicenseId(null);
    resetForm();
  };

  const openDeleteModal = (license) => {
    setDeletingLicense(license);
    setDeleting(false);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModalOpen(false);
    setDeletingLicense(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    
    setSaving(true);
    
    try {
      const payload = {
        site_id: form.site_id || null,
        max_stations: parseInt(form.max_stations) || 10,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        is_active: form.is_active,
        price: parseFloat(form.price) || 0,
        currency: form.currency || 'USD',
        notes: form.notes || null,
        warn_days_before: parseInt(form.warn_days_before) || 30,
        warning_method: form.warning_method || 'both',
        warning_message: form.warning_message || null
      };

      if (modalMode === 'create') {
        // Don't send license_key - backend will auto-generate
        await licenseService.create(payload);
        toast.success('Lisans başarıyla oluşturuldu');
      } else {
        await licenseService.update(editingLicenseId, payload);
        toast.success('Lisans başarıyla güncellendi');
      }
      
      closeModal();
      fetchData();
      
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingLicense || deleting) return;
    
    setDeleting(true);
    
    try {
      await licenseService.delete(deletingLicense.id);
      toast.success('Lisans başarıyla silindi');
      closeDeleteModal();
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleSendWarnings = async () => {
    setSendingWarnings(true);
    try {
      const result = await licenseStats.sendWarnings();
      toast.success(result.message);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSendingWarnings(false);
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || '-';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const getDaysRemaining = (dateStr) => {
    if (!dateStr) return null;
    const expiry = new Date(dateStr);
    const now = new Date();
    const diff = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getDaysRemainingBadge = (dateStr) => {
    const days = getDaysRemaining(dateStr);
    if (days === null) return null;
    
    let bgColor, textColor = 'white';
    if (days < 0) {
      bgColor = '#dc2626';
      return <span style={{ backgroundColor: bgColor, color: textColor, padding: '2px 8px', borderRadius: '9999px', fontSize: '12px' }}>Süresi Doldu</span>;
    } else if (days <= 7) {
      bgColor = '#dc2626';
    } else if (days <= 15) {
      bgColor = '#f97316';
    } else if (days <= 30) {
      bgColor = '#eab308';
      textColor = 'black';
    } else {
      bgColor = '#22c55e';
    }
    return <span style={{ backgroundColor: bgColor, color: textColor, padding: '2px 8px', borderRadius: '9999px', fontSize: '12px' }}>{days} gün kaldı</span>;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const symbol = CURRENCY_SYMBOLS[currency] || '$';
    return `${symbol}${(amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`;
  };

  const formatTotalByCurrency = (totals) => {
    if (!totals || Object.keys(totals).length === 0) return '-';
    return Object.entries(totals)
      .map(([currency, amount]) => formatCurrency(amount, currency))
      .join(' + ');
  };

  const filteredLicenses = licenses.filter(license => 
    license.license_key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    license.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const warningLicenses = getWarningLicenses();

  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <div style={{ width: '32px', height: '32px', border: '3px solid #2563eb', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: '16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626' }}>
        <strong>Hata:</strong> {error}
        <NativeButton onClick={fetchData} variant="secondary" style={{ marginLeft: '16px' }}>Yenile</NativeButton>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Lisans Yönetimi</h1>
          <p style={{ color: '#6b7280', margin: '4px 0 0' }}>Sistem lisanslarını yönetin</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {isEffectiveSuperAdmin && isSuperAdmin() && (
            <>
              <NativeButton onClick={handleSendWarnings} variant="secondary" disabled={sendingWarnings}>
                {sendingWarnings ? 'Gönderiliyor...' : '📧 Uyarıları Gönder'}
              </NativeButton>
              <NativeButton onClick={fetchData} variant="secondary">🔄 Yenile</NativeButton>
              <NativeButton onClick={openCreateModal} data-testid="create-license-btn">+ Yeni Lisans</NativeButton>
            </>
          )}
          {(!isEffectiveSuperAdmin || !isSuperAdmin()) && (
            <NativeButton onClick={fetchData} variant="secondary">🔄 Yenile</NativeButton>
          )}
        </div>
      </div>

      {/* Warning Licenses */}
      {warningLicenses.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          {warningLicenses.map(lic => {
            const days = getDaysRemaining(lic.expires_at);
            return (
              <div key={lic.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                backgroundColor: days <= 7 ? '#fef2f2' : '#fffbeb',
                border: `1px solid ${days <= 7 ? '#fecaca' : '#fde68a'}`,
                borderRadius: '8px',
                marginBottom: '8px'
              }}>
                <div>
                  <strong style={{ fontFamily: 'monospace' }}>{lic.license_key}</strong>
                  <span style={{ marginLeft: '12px', color: '#6b7280' }}>
                    {days} gün kaldı ({formatDate(lic.expires_at)})
                  </span>
                </div>
                <button
                  onClick={() => dismissWarning(lic.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ padding: '16px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid var(--card-border)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Toplam Aktif Gelir</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#16a34a' }}>{formatTotalByCurrency(stats.total_active_revenue)}</div>
          </div>
          <div style={{ padding: '16px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid var(--card-border)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Bu Ay Dolacak</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f97316' }}>{formatTotalByCurrency(stats.expiring_this_month_total)}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{stats.expiring_this_month?.length || 0} lisans</div>
          </div>
          <div style={{ padding: '16px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid var(--card-border)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>30 Gün İçinde</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#eab308' }}>{formatTotalByCurrency(stats.expiring_30_days_total)}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{stats.expiring_30_days?.length || 0} lisans</div>
          </div>
          <div style={{ padding: '16px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid var(--card-border)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Kaybedilen Gelir</div>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#dc2626' }}>{formatTotalByCurrency(stats.expired_revenue_lost)}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{stats.expired_licenses?.length || 0} süresi dolmuş</div>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Lisans ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '300px',
            padding: '8px 12px',
            borderRadius: '6px',
            border: '1px solid var(--card-border)',
            fontSize: '14px',
            backgroundColor: 'var(--card-bg)',
            color: 'var(--text-primary)'
          }}
          data-testid="license-search-input"
        />
      </div>

      {/* Licenses Table */}
      <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden', border: '1px solid var(--card-border)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--card-bg)', borderBottom: '1px solid var(--card-border)' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Lisans Anahtarı</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Site</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Cihaz/Fiyat</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Aylık Toplam</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Son Kullanma</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>Durum</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)' }}>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {filteredLicenses.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Lisans bulunamadı
                </td>
              </tr>
            ) : (
              filteredLicenses.map((license) => {
                const unitPrice = license.unit_price_per_device || 5;
                const activeStations = license.active_station_count || 0;
                const monthlyTotal = unitPrice * activeStations;
                const currencySymbols = { USD: '$', EUR: '€', TRY: '₺', GBP: '£' };
                const symbol = currencySymbols[license.currency] || '₺';
                
                return (
                <tr key={license.id} style={{ borderBottom: '1px solid var(--card-border)' }}>
                  <td style={{ padding: '12px 16px', color: 'var(--text-primary)' }}>
                    <code style={{ backgroundColor: 'var(--card-bg)', padding: '4px 8px', borderRadius: '4px', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', border: '1px solid var(--card-border)' }}>
                      {license.license_key}
                    </code>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-primary)' }}>{getSiteName(license.site_id)}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-primary)' }}>
                    <div style={{ fontSize: '14px' }}>
                      <span style={{ fontWeight: '600', color: '#059669' }}>{activeStations}</span>
                      <span style={{ color: 'var(--text-secondary)' }}> / {license.max_stations || 10} cihaz</span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {symbol}{unitPrice.toFixed(2)}/cihaz/ay
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: '700', fontSize: '16px', color: '#059669' }}>
                      {symbol}{monthlyTotal.toFixed(2)}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      /ay
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-primary)' }}>
                    <div style={{ color: 'var(--text-primary)' }}>{formatDate(license.expires_at)}</div>
                    <div style={{ marginTop: '4px' }}>{getDaysRemainingBadge(license.expires_at)}</div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: license.is_active ? '#22c55e' : '#6b7280',
                      color: 'white'
                    }}>
                      {license.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {isEffectiveSuperAdmin && isSuperAdmin() && (
                      <>
                        <NativeButton
                          variant="ghost"
                          onClick={() => openEditModal(license)}
                          data-testid={`edit-license-${license.id}`}
                          style={{ marginRight: '4px' }}
                        >
                          ✏️
                        </NativeButton>
                        <NativeButton
                          variant="ghost"
                          onClick={() => openDeleteModal(license)}
                          data-testid={`delete-license-${license.id}`}
                          style={{ color: '#dc2626' }}
                        >
                          🗑️
                        </NativeButton>
                      </>
                    )}
                    {(!isEffectiveSuperAdmin || !isSuperAdmin()) && (
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>Salt okunur</span>
                    )}
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      <NativeModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Yeni Lisans Oluştur' : 'Lisans Düzenle'}
        disabled={saving}
      >
        <form onSubmit={handleSubmit}>
          {/* Info box for create mode */}
          {modalMode === 'create' && (
            <div style={{
              padding: '12px',
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#1e40af'
            }}>
              ℹ️ Lisans anahtarı sistem tarafından otomatik oluşturulacaktır.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <NativeSelect
              label="Site"
              id="site_id"
              value={form.site_id}
              onChange={(e) => updateForm('site_id', e.target.value)}
              options={[
                { value: '', label: 'Site Seçin' },
                ...sites.map(s => ({ value: s.id, label: s.name }))
              ]}
              data-testid="license-site-select"
            />

            <NativeInput
              label="Max İstasyon"
              id="max_stations"
              type="number"
              min="1"
              value={form.max_stations}
              onChange={(e) => updateForm('max_stations', e.target.value)}
              data-testid="license-max-stations-input"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <NativeInput
              label="Son Kullanma Tarihi"
              id="expires_at"
              type="date"
              value={form.expires_at}
              onChange={(e) => updateForm('expires_at', e.target.value)}
              data-testid="license-expires-input"
            />

            <NativeCheckbox
              label="Aktif"
              id="is_active"
              checked={form.is_active}
              onChange={(e) => updateForm('is_active', e.target.checked)}
              data-testid="license-active-checkbox"
            />
          </div>

          {/* Price Section */}
          <div style={{ padding: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', marginBottom: '16px', border: '1px solid #bbf7d0' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600', color: '#166534' }}>💰 Fiyat Bilgileri</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <NativeInput
                label="Fiyat"
                labelStyle={{ color: '#166534' }}
                id="price"
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={(e) => updateForm('price', e.target.value)}
                data-testid="license-price-input"
              />
              <NativeSelect
                label="Para Birimi"
                labelStyle={{ color: '#166534' }}
                id="currency"
                value={form.currency}
                onChange={(e) => updateForm('currency', e.target.value)}
                options={[
                  { value: 'USD', label: '$ USD (Dolar)' },
                  { value: 'EUR', label: '€ EUR (Euro)' },
                  { value: 'TRY', label: '₺ TRY (Türk Lirası)' },
                  { value: 'GBP', label: '£ GBP (Sterlin)' }
                ]}
                data-testid="license-currency-select"
              />
            </div>
          </div>

          {/* Warning Settings */}
          <div style={{ padding: '16px', backgroundColor: '#fff7ed', borderRadius: '8px', marginBottom: '16px', border: '1px solid #fed7aa' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600', color: '#9a3412' }}>🔔 Uyarı Ayarları</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <NativeSelect
                label="Kaç Gün Önce Uyar"
                labelStyle={{ color: '#9a3412' }}
                id="warn_days_before"
                value={form.warn_days_before}
                onChange={(e) => updateForm('warn_days_before', e.target.value)}
                options={[
                  { value: '7', label: '7 gün önce' },
                  { value: '15', label: '15 gün önce' },
                  { value: '30', label: '30 gün önce' },
                  { value: '45', label: '45 gün önce' },
                  { value: '60', label: '60 gün önce' },
                  { value: '90', label: '90 gün önce' }
                ]}
                data-testid="license-warn-days-input"
              />
              <NativeSelect
                label="Uyarı Yöntemi"
                labelStyle={{ color: '#9a3412' }}
                id="warning_method"
                value={form.warning_method}
                onChange={(e) => updateForm('warning_method', e.target.value)}
                options={[
                  { value: 'both', label: '📧 Email + 🔔 Sistem' },
                  { value: 'email', label: '📧 Sadece Email' },
                  { value: 'system', label: '🔔 Sadece Sistem' }
                ]}
                data-testid="license-warning-method-select"
              />
            </div>
            <NativeTextarea
              label="Uyarı Mesajı"
              labelStyle={{ color: '#9a3412' }}
              id="warning_message"
              value={form.warning_message}
              onChange={(e) => updateForm('warning_message', e.target.value)}
              placeholder="Örn: Sistem kapatılacaktır. Lütfen yenileme işlemlerini başlatın."
              rows={2}
              data-testid="license-warning-message-input"
            />
          </div>

          {/* Notes */}
          <NativeTextarea
            label="Notlar"
            id="notes"
            value={form.notes}
            onChange={(e) => updateForm('notes', e.target.value)}
            placeholder="Lisansla ilgili notlar..."
            rows={2}
            data-testid="license-notes-input"
          />

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
            <NativeButton type="button" variant="secondary" onClick={closeModal} disabled={saving}>
              İptal
            </NativeButton>
            <NativeButton type="submit" disabled={saving} data-testid="license-submit-button">
              {saving ? 'Kaydediliyor...' : (modalMode === 'create' ? 'Oluştur' : 'Kaydet')}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Delete Confirmation Modal */}
      <NativeModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        title="Lisansı Sil"
        disabled={deleting}
      >
        {deletingLicense && (
          <div>
            <p style={{ marginBottom: '12px' }}>
              <strong style={{ fontFamily: 'monospace' }}>{deletingLicense.license_key}</strong> lisansını silmek istediğinize emin misiniz?
            </p>
            {deletingLicense.price > 0 && (
              <p style={{ color: '#f97316', marginBottom: '12px' }}>
                ⚠️ Bu lisansın değeri: {formatCurrency(deletingLicense.price, deletingLicense.currency)}
              </p>
            )}
            <p style={{ color: '#6b7280', fontSize: '14px' }}>Bu işlem geri alınamaz.</p>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '16px' }}>
              <NativeButton type="button" variant="secondary" onClick={closeDeleteModal} disabled={deleting}>
                İptal
              </NativeButton>
              <NativeButton
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting}
                data-testid="confirm-delete-button"
              >
                {deleting ? 'Siliniyor...' : 'Sil'}
              </NativeButton>
            </div>
          </div>
        )}
      </NativeModal>
    </div>
  );
}
